/**
 * Which psalm is appointed on which liturgical day — the join that makes a psalm
 * page worth visiting.
 *
 * Psalm 25 on its own is marginally useful. *Psalm 25 linked to the days it is
 * appointed for* is the thing a church musician actually searches for, and the
 * thing that lets a service plan pre-fill its psalm slot. This module is that join,
 * kept separate from `psalter-pages.ts` for one reason: **they have different
 * copyright answers, and mixing them would hide that.**
 *
 * ## THE RIGHTS GATE — read before importing this
 *
 * The psalter TEXT (`psalter-pages.ts`) is 1979 BCP and public domain in the US.
 * **The table of which readings fall on which day is not.** It is
 * © 1992 Consultation on Common Texts, administered by Augsburg Fortress, and
 * `data/lectionary/PROVENANCE.md` states the position plainly:
 *
 * > CCT allows free reproduction by non-profit, non-commercial groups and by member
 * > denominations' publishers; Fugue Lab is neither, so a commercial or lead-gen
 * > surface needs written permission.
 *
 * `fuguelab.com/hymnal/*` is a lead-gen surface. So this module is deliberately
 * built to produce NOTHING until a permission exists, and the gate is a **value,
 * not a boolean flag**: `extractPsalmAppointments` takes an `RclPermission` and
 * returns `[]` for `null`. Turning the join on therefore means writing down who
 * granted it and when, in a diff a reviewer can see — rather than flipping a
 * `true` that nobody can trace back to a decision.
 *
 * When permission lands, replace `RCL_PERMISSION_NONE` at the call site with a
 * populated `RclPermission` and render `attributionLine()` wherever the data shows.
 * Do not delete the gate; a future surface will need to ask the same question.
 *
 * Pure and data-injected: nothing here imports a dataset.
 */
/**
 * The absence of permission, named. Passing this is not an oversight — it is the
 * current, deliberate state, and it reads that way in a diff.
 */
export const RCL_PERMISSION_NONE = null;
/** The notice CCT requires wherever the table is reproduced. Non-optional. */
export function attributionLine() {
    return 'Revised Common Lectionary, Copyright © 1992 Consultation on Common Texts. Used by permission.';
}
const isYear = (c) => c === 'A' || c === 'B' || c === 'C';
/**
 * The lectionary file writes day ids with DOTS (`advent.1`, `christmas.holy-name`)
 * while propers pages and `canonicalDayId()` use DASHES (`advent-1`). Joining on the
 * wrong one silently produces zero matches — no error, just an empty index — so the
 * normalization is here, once, rather than at each call site.
 */
export function normalizeDayId(day) {
    return day.trim().toLowerCase().replace(/\./g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
}
/** Canticles the RCL appoints in the psalm slot, by the citation that names them. */
const CANTICLE_CITATIONS = [/^luke\s+1:46/i, /^luke\s+1:68/i, /^isaiah\s+12/i, /^lamentations\s+3/i];
function classify(citation) {
    const m = citation.trim().match(/^(?:psalms?\s+)?(\d{1,3})\b/i);
    const n = m ? Number(m[1]) : NaN;
    if (Number.isInteger(n) && n >= 1 && n <= 150 && /^(?:psalms?\s+)?\d/i.test(citation.trim())) {
        return { kind: 'psalm', psalmNumber: n };
    }
    if (CANTICLE_CITATIONS.some((re) => re.test(citation.trim())))
        return { kind: 'canticle', psalmNumber: null };
    return { kind: 'other', psalmNumber: null };
}
/**
 * Extract every psalm appointment — or nothing at all, if no permission exists.
 *
 * The empty return is the POINT, not a failure mode. A caller that renders whatever
 * this hands back is automatically compliant, and turning the join on is a
 * single reviewable edit at the call site rather than a hunt through render code.
 */
export function extractPsalmAppointments(days, permission) {
    if (!permission)
        return [];
    const out = [];
    for (const d of days) {
        const citation = d.readings?.psalm?.citation?.trim();
        if (!citation || !isYear(d.cycle))
            continue;
        const { kind, psalmNumber } = classify(citation);
        out.push({
            cycle: d.cycle,
            dayId: normalizeDayId(d.day),
            dayTitle: d.title,
            season: d.season,
            citation,
            psalmNumber,
            kind,
        });
    }
    // Deterministic: same file in, byte-identical index out, so SSR and browser
    // renders match and a rebuild produces no spurious diff.
    out.sort((a, b) => a.dayId.localeCompare(b.dayId) || a.cycle.localeCompare(b.cycle));
    return out;
}
/** psalm number → the days it is appointed for. The reverse index; C3's whole point. */
export function byPsalm(appointments) {
    const m = new Map();
    for (const a of appointments) {
        if (a.psalmNumber === null)
            continue;
        const list = m.get(a.psalmNumber);
        if (list)
            list.push(a);
        else
            m.set(a.psalmNumber, [a]);
    }
    return m;
}
/** day id → what is appointed there, across all three cycle years. */
export function byDay(appointments) {
    const m = new Map();
    for (const a of appointments) {
        const list = m.get(a.dayId);
        if (list)
            list.push(a);
        else
            m.set(a.dayId, [a]);
    }
    for (const list of m.values())
        list.sort((x, y) => x.cycle.localeCompare(y.cycle));
    return m;
}
